/**
 * A spoken turn's panel, applied (composer panels, spec 2026-09-19 §3.4): the
 * crown holds a drawn kind; history and tray open the door and the pill (the
 * call stays live), the way the ⌃⌃ pair does; close takes down everything the
 * line put up. Kinds nobody draws yet (connect, confirm in Train 1) are held too, so
 * Train 2 needs no handler change; the model decides what is up.
 */
import type { ComposerPanel } from "@bash/shared";
import { openPillDoor } from "@/components/bash-home/pillPanels";
import { isDockVisible } from "@/lib/home-flags";
import { useLiveStore, type LineDoorPanel } from "./liveStore";

/** The route the person is on, as the Dock reads it. Null before the window
 *  exists; isDockVisible treats that as a dock route, the shipped default. */
const currentPath = (): string | null =>
  typeof window === "undefined" ? null : window.location.pathname;

/** The door a spoken history or tray opens: the box for the history (the ⌃⌃
 *  pattern's other half), the Island for the tray. One place, so the door
 *  opened on the spot and the one parked for /home can never drift. */
export function openLineDoor(kind: LineDoorPanel): void {
  const live = useLiveStore.getState();
  if (kind === "history" && live.door === "ball") {
    live.openDoor();
    // Whose door it is decides whether "close it now" may shut it again.
    useLiveStore.setState({ doorOpenedByLine: true });
  }
  openPillDoor(kind);
}

export function applyLinePanel(panel: ComposerPanel): void {
  const live = useLiveStore.getState();
  switch (panel.kind) {
    case "history":
    case "tray":
      // The Dock paints nothing on /task and /support (the ⌃⌃ pair and the ⌥
      // island are gated on that too), so flipping a level there opens a box
      // nobody can see and makes "Your history is open." a lie, and the next
      // dock route would find it up unasked. Ride to /home the way a spoken
      // app_control moves you, then open it there (R1).
      if (isDockVisible(currentPath())) openLineDoor(panel.kind);
      else live.setPendingDoor(panel.kind);
      return;
    case "close":
      // Close means every panel the line put up, not just the crown's card
      // (the founder's call, 2026-09-19: "Close it now" left the history on
      // screen while the mind said it was closed). The box is the one thing
      // it may not always take: a door opened THROUGH the line for a spoken
      // history goes, a door they opened themselves is theirs and stays.
      live.setPanel(null);
      live.closeHistory();
      live.setTrayOpen(false);
      live.setPendingDoor(null);
      if (live.doorOpenedByLine) live.closeDoor();
      return;
    default:
      live.setPanel(panel);
  }
}
